import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';

// "How many metres make a full spool" per material / diameter / spool weight. The winder
// classifier compares every finished cycle against these rows: within nominal ± tolerance
// = full spool (+1 pc), shorter = the operator is asked whether it is waste.

interface Spec {
  id: number;
  material: string;
  diameter_mm: string;
  spool_weight_kg: string;
  nominal_length_m: string;
  tolerance_m: string;
  grams_per_meter: string | null;
  is_active: boolean;
}

interface FormState {
  material: string;
  diameter_mm: string;
  spool_weight_kg: string;
  nominal_length_m: string;
  tolerance_m: string;
  grams_per_meter: string;
  is_active: boolean;
}

const EMPTY: FormState = { material: '', diameter_mm: '1.75', spool_weight_kg: '1', nominal_length_m: '', tolerance_m: '10', grams_per_meter: '', is_active: true };

const MaterialSpoolSpecs: React.FC = () => {
  const { t } = useLanguage();
  const [specs, setSpecs] = useState<Spec[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [editing, setEditing] = useState<Spec | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSpecs = useCallback(async () => {
    try {
      const res = await api.get('/material-spool-specs');
      setSpecs(res.data.specs);
    } catch {
      setSpecs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSpecs(); }, [fetchSpecs]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setError(null); setShowForm(true); };
  const openEdit = (s: Spec) => {
    setEditing(s);
    setForm({
      material: s.material, diameter_mm: s.diameter_mm, spool_weight_kg: s.spool_weight_kg,
      nominal_length_m: s.nominal_length_m, tolerance_m: s.tolerance_m,
      grams_per_meter: s.grams_per_meter ?? '', is_active: s.is_active,
    });
    setError(null);
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      material: form.material.trim(),
      diameter_mm: parseFloat(form.diameter_mm.replace(',', '.')),
      spool_weight_kg: parseFloat(form.spool_weight_kg.replace(',', '.')),
      nominal_length_m: parseFloat(form.nominal_length_m.replace(',', '.')),
      tolerance_m: parseFloat(form.tolerance_m.replace(',', '.') || '0'),
      grams_per_meter: form.grams_per_meter.trim() ? parseFloat(form.grams_per_meter.replace(',', '.')) : null,
      is_active: form.is_active,
    };
    try {
      if (editing) await api.put(`/material-spool-specs/${editing.id}`, payload);
      else await api.post('/material-spool-specs', payload);
      setShowForm(false);
      fetchSpecs();
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      setError(errors ? Object.values(errors).flat().join(' ') : (err?.response?.data?.message ?? t('spoolSpecs.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: Spec) => {
    if (!window.confirm(t('spoolSpecs.confirmDelete', { material: s.material }))) return;
    try {
      await api.delete(`/material-spool-specs/${s.id}`);
      fetchSpecs();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? t('spoolSpecs.saveError'));
    }
  };

  // Derived g/m when not given — the same rule the backend uses to estimate waste mass.
  const gpm = (s: Spec): string => {
    if (s.grams_per_meter) return parseFloat(s.grams_per_meter).toFixed(3);
    const len = parseFloat(s.nominal_length_m);
    return len > 0 ? ((parseFloat(s.spool_weight_kg) * 1000) / len).toFixed(3) : '—';
  };

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>🧵 {t('spoolSpecs.title')}</h1>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('spoolSpecs.subtitle')}</p>
        </div>
        <button onClick={openNew} style={{ padding: '10px 18px', background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          + {t('spoolSpecs.add')}
        </button>
      </div>

      {error && !showForm && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#991b1b' }}>{error}</div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={th}>{t('spoolSpecs.colMaterial')}</th>
              <th style={th}>{t('spoolSpecs.colDiameter')}</th>
              <th style={th}>{t('spoolSpecs.colWeight')}</th>
              <th style={th}>{t('spoolSpecs.colNominal')}</th>
              <th style={th}>{t('spoolSpecs.colTolerance')}</th>
              <th style={th}>{t('spoolSpecs.colWindow')}</th>
              <th style={th}>g/m</th>
              <th style={th}>{t('spoolSpecs.colActive')}</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>{t('common.loading')}</td></tr>
            ) : specs.length === 0 ? (
              <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>{t('spoolSpecs.empty')}</td></tr>
            ) : specs.map(s => {
              const nominal = parseFloat(s.nominal_length_m);
              const tol = parseFloat(s.tolerance_m);
              return (
                <tr key={s.id} style={{ opacity: s.is_active ? 1 : 0.5 }}>
                  <td style={{ ...td, fontWeight: 700 }}>{s.material}</td>
                  <td style={td}>{parseFloat(s.diameter_mm).toFixed(2)} mm</td>
                  <td style={td}>{parseFloat(s.spool_weight_kg).toFixed(2)} kg</td>
                  <td style={{ ...td, fontWeight: 700 }}>{nominal.toFixed(1)} m</td>
                  <td style={td}>± {tol.toFixed(1)} m</td>
                  <td style={{ ...td, color: '#059669', fontWeight: 600 }}>{(nominal - tol).toFixed(1)} – {(nominal + tol).toFixed(1)} m</td>
                  <td style={td}>{gpm(s)}</td>
                  <td style={td}>{s.is_active ? '✔' : '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => openEdit(s)} style={btnSmall}>✏️ {t('spoolSpecs.edit')}</button>
                    <button onClick={() => remove(s)} style={{ ...btnSmall, color: '#dc3545', marginLeft: 6 }}>🗑</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 520, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: 20, color: '#1e293b' }}>{editing ? t('spoolSpecs.editTitle') : t('spoolSpecs.addTitle')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label style={{ ...lbl, gridColumn: '1 / -1' }}>{t('spoolSpecs.colMaterial')}
                <input value={form.material} onChange={e => setForm({ ...form, material: e.target.value })} placeholder="PLA" style={inp} />
                <span style={hint}>{t('spoolSpecs.materialHint')}</span>
              </label>
              <label style={lbl}>{t('spoolSpecs.colDiameter')} [mm]
                <input value={form.diameter_mm} onChange={e => setForm({ ...form, diameter_mm: e.target.value })} style={inp} />
              </label>
              <label style={lbl}>{t('spoolSpecs.colWeight')} [kg]
                <input value={form.spool_weight_kg} onChange={e => setForm({ ...form, spool_weight_kg: e.target.value })} style={inp} />
              </label>
              <label style={lbl}>{t('spoolSpecs.colNominal')} [m]
                <input value={form.nominal_length_m} onChange={e => setForm({ ...form, nominal_length_m: e.target.value })} placeholder="345" style={inp} />
              </label>
              <label style={lbl}>{t('spoolSpecs.colTolerance')} [± m]
                <input value={form.tolerance_m} onChange={e => setForm({ ...form, tolerance_m: e.target.value })} style={inp} />
              </label>
              <label style={{ ...lbl, gridColumn: '1 / -1' }}>g/m ({t('spoolSpecs.optional')})
                <input value={form.grams_per_meter} onChange={e => setForm({ ...form, grams_per_meter: e.target.value })} placeholder={t('spoolSpecs.gpmPlaceholder')} style={inp} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569', gridColumn: '1 / -1' }}>
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} /> {t('spoolSpecs.colActive')}
              </label>
            </div>
            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', margin: '12px 0 0', fontSize: 13, color: '#991b1b' }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button onClick={save} disabled={saving} style={{ flex: 1, padding: '12px 0', background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? '...' : t('spoolSpecs.save')}
              </button>
              <button onClick={() => setShowForm(false)} disabled={saving} style={{ flex: 1, padding: '12px 0', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                {t('spoolSpecs.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#6c757d', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap', letterSpacing: 0.5 };
const td: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' };
const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#475569' };
const inp: React.CSSProperties = { padding: '9px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 };
const hint: React.CSSProperties = { fontSize: 11, fontWeight: 400, color: '#94a3b8' };
const btnSmall: React.CSSProperties = { padding: '5px 10px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#475569', cursor: 'pointer' };

export default MaterialSpoolSpecs;
